// A compact emoji grid for the chat composer: the ones you used last first (remembered on this device), then a
// few groups. Built in, drawn by the system's emoji font. Pressing one never takes the focus from the message
// (so the caret and an open phone keyboard stay put); opened from the keyboard, the arrow keys move around it.
import { useEffect, useMemo, useRef, type KeyboardEvent } from 'react';

const GROUPS: Array<{ label: string; list: string[] }> = [
  { label: 'Smileys', list: '😀 😃 😄 😁 😆 😅 😂 🤣 😊 🙂 😉 😍 😘 😎 🤩 🥳 🤔 🤨 😐 🙄 😏 😴 😮 😲 😬 😢 😭 😤 😡 🤯 😇 🤗'.split(' ') },
  { label: 'Hands', list: '👍 👎 👌 ✌️ 🤞 🤝 👏 🙌 👋 🙏 💪 👀 ☝️ 👉 👈 👆 👇 ✋ 🤙 ✍️'.split(' ') },
  { label: 'Symbols', list: '❤️ 🧡 💛 💚 💙 💜 🖤 💯 🔥 ⭐ ✨ ✅ ❌ ⚠️ ❗ ❓ ⏰ ⏳ 🎉 🎂 🍀 ☕ 🍕 🍻'.split(' ') },
  { label: 'Work', list: '🚤 ⛵ 🛥️ 🚢 ⚓ 🌊 ☀️ 🌧️ 💨 🔧 🔩 ⚙️ 🛠️ 🧰 🔌 🔋 ⛽ 📦 🚚 📋 📅 📌 📎 ✏️ 📝 📷 📞 💡 💶 📈 📉 🏁'.split(' ') },
];
const COLS = 8;
const RECENT_KEY = 'rolodex.chat.emoji';
const RECENT_MAX = 16;

function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length <= 16).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}
/** put an emoji at the front of this device's recent list. */
export function rememberEmoji(emoji: string): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([emoji, ...readRecent().filter((x) => x !== emoji)].slice(0, RECENT_MAX)));
  } catch { /* storage blocked: no recent row */ }
}

/** `onClose` is told whether the grid was closed from its own keys (Escape) rather than by a press elsewhere. */
export function EmojiPicker({ onPick, onClose, focusFirst }: { onPick: (emoji: string) => void; onClose: (byKey: boolean) => void; focusFirst: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const recent = useMemo(readRecent, []);
  const groups = useMemo(() => (recent.length ? [{ label: 'Recent', list: recent }, ...GROUPS] : GROUPS), [recent]);

  useEffect(() => { if (focusFirst) ref.current?.querySelector<HTMLButtonElement>('button')?.focus(); }, [focusFirst]);
  // a press anywhere else closes it
  useEffect(() => {
    const down = (e: PointerEvent) => { if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as Element)?.closest?.('[data-emoji-toggle]')) onClose(false); };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, [onClose]);

  // arrow keys move through the whole grid, a row being COLS wide
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true); return; }
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: COLS, ArrowUp: -COLS }[e.key];
    if (!step) return;
    const all = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    e.preventDefault();
    all[Math.max(0, Math.min(all.length - 1, at + step))]?.focus();
  };

  return (
    <div ref={ref} className="chat-pop chat-emoji" role="dialog" aria-label="Emoji" onKeyDown={onKeyDown}>
      {groups.map((g) => (
        <div key={g.label} className="chat-emoji-group" role="group" aria-label={g.label}>
          <div className="chat-emoji-hd">{g.label}</div>
          <div className="chat-emoji-grid">
            {g.list.map((e) => (
              <button
                key={`${g.label}${e}`}
                type="button"
                className="chat-emoji-btn"
                aria-label={e}
                onPointerDown={(ev) => ev.preventDefault()}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => onPick(e)}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
